import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CategoryPicker } from '../components/CategoryPicker'
import { Screen } from '../components/Layout'
import { Button, Card, ErrorBanner, Field, Spinner, inputClass } from '../components/ui'
import { money, parseAmount, todayIso } from '../lib/format'
import { useCategories, useCreateExpense, useMe } from '../lib/queries'

/**
 * Manual entry (requirement 2), with the optional split (requirement 5/6).
 *
 * The split section defaults to an equal share and only sends `shareMinor` when
 * the user overrides it -- omitting the field is what tells the API this is an
 * equal split, and it recomputes the halves itself. Sending a client-computed
 * "half" would make the payer absorb rounding the server never agreed to.
 */
export function AddExpensePage() {
  const navigate = useNavigate()
  const taxonomy = useCategories()
  const me = useMe()
  const create = useCreateExpense()

  const [date, setDate] = useState(todayIso())
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState<{ categoryId: string; subCategoryId?: string }>({
    categoryId: 'food',
  })
  const [merchant, setMerchant] = useState('')
  const [note, setNote] = useState('')
  const [splitPairId, setSplitPairId] = useState('')
  const [customShare, setCustomShare] = useState('')

  const amountMinor = parseAmount(amount)
  const shareMinor = customShare.trim() === '' ? undefined : parseAmount(customShare)
  const linkable = me.data?.members ?? []
  const shareTooBig =
    amountMinor !== undefined && shareMinor !== undefined && shareMinor > amountMinor

  const canSubmit =
    amountMinor !== undefined &&
    !shareTooBig &&
    (customShare.trim() === '' || shareMinor !== undefined) &&
    !create.isPending

  function submit(): void {
    if (amountMinor === undefined) return
    create.mutate(
      {
        date,
        amountMinor,
        categoryId: category.categoryId,
        ...(category.subCategoryId ? { subCategoryId: category.subCategoryId } : {}),
        ...(merchant.trim() ? { merchant: merchant.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(splitPairId
          ? {
              splits: [
                {
                  pairId: splitPairId,
                  ...(shareMinor !== undefined ? { shareMinor } : {}),
                },
              ],
            }
          : {}),
      },
      { onSuccess: () => navigate('/', { replace: true }) },
    )
  }

  return (
    <Screen title="記一筆">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <Card className="space-y-4 p-4">
          <Field label="金額" hint="新臺幣沒有小數，請輸入整數。">
            <input
              className={`${inputClass} tnum text-2xl`}
              inputMode="decimal"
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              aria-label="金額"
            />
          </Field>
          {amount.trim() !== '' && amountMinor === undefined ? (
            <p className="text-xs text-owing">請輸入有效的金額。</p>
          ) : null}

          <Field label="日期">
            <input
              type="date"
              className={inputClass}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-label="日期"
            />
          </Field>
        </Card>

        <div>
          <p className="mb-1 text-sm text-muted">分類</p>
          {taxonomy.isLoading ? (
            <Spinner />
          ) : taxonomy.data ? (
            <CategoryPicker
              taxonomy={taxonomy.data}
              categoryId={category.categoryId}
              {...(category.subCategoryId ? { subCategoryId: category.subCategoryId } : {})}
              onChange={setCategory}
            />
          ) : (
            <ErrorBanner error={taxonomy.error} onRetry={() => void taxonomy.refetch()} />
          )}
        </div>

        <Card className="space-y-4 p-4">
          <Field label="店家（選填）">
            <input
              className={inputClass}
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              placeholder="全聯門市"
            />
          </Field>
          <Field label="備註（選填）">
            <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </Card>

        {linkable.length > 0 ? (
          <Card className="space-y-4 p-4">
            <Field label="分帳（選填）">
              <select
                className={inputClass}
                value={splitPairId}
                onChange={(e) => setSplitPairId(e.target.value)}
              >
                <option value="">不分帳</option>
                {linkable.map((member) => (
                  <option key={member.pairId} value={member.pairId}>
                    與 {member.displayName} 分帳
                  </option>
                ))}
              </select>
            </Field>

            {splitPairId ? (
              <Field
                label="對方負擔金額（選填）"
                hint={
                  amountMinor === undefined
                    ? '留空則各付一半。'
                    : `留空則各付一半：對方 ${money(Math.floor(amountMinor / 2))}。`
                }
              >
                <input
                  className={`${inputClass} tnum`}
                  inputMode="decimal"
                  value={customShare}
                  onChange={(e) => setCustomShare(e.target.value)}
                  placeholder="平分"
                />
              </Field>
            ) : null}
            {shareTooBig ? (
              <p className="text-xs text-owing">對方負擔金額不能超過總金額。</p>
            ) : null}
          </Card>
        ) : null}

        {/*
          Pinned to the bottom of the scroll area, so 儲存 is reachable without
          scrolling past the split section. `sticky` rather than `fixed`: the shell
          gives `<main>` the overflow, so this lands exactly on top of the tab bar
          with no height to measure and nothing to drift when the keyboard opens.

          The error lives here too. It used to sit at the top of the form, which on
          a phone means a failed save looks like nothing happening at all.
        */}
        <div className="sticky bottom-0 -mx-4 space-y-3 border-t border-ink-line/60 bg-ink/95 px-4 py-3 backdrop-blur">
          <ErrorBanner error={create.error} />
          <Button type="submit" className="w-full" disabled={!canSubmit}>
            {create.isPending ? '儲存中…' : '儲存'}
          </Button>
        </div>
      </form>
    </Screen>
  )
}
